/* istanbul ignore file */

import PropTypes, { func } from 'prop-types';
import React, { useCallback } from 'react';

import {
  Delete16Regular,
  AddSquare24Regular,
  ErrorCircle20Regular,
  CheckmarkCircle20Regular,
} from '@fluentui/react-icons';
import { Stack } from '@bingads-webui-react/fabric-control';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { FilePicker } from '@bingads-webui-campaign-react/upload-file-picker';
import {
  Button,
  ProgressBar,
  Text,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import { EntryCardSVGIcons } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';

import {
  useFileUploadStyles,
  filesPerRowInPreviewGrid,
  getFileUploadButtonStyles,
  getFileUploadContainerStyles,
} from './style';
import { LegacyStringMap } from './constants';
import { formatFileSize, uploadFiles, validateFiles } from './utils';

const FilePreview = ({
  i18n,
  file,
  progress,
  onDeleteFile,
}) => {
  const classes = useFileUploadStyles();
  const hasError = !!file.errorType;
  const uploadTried = typeof file.uploaded === 'boolean';

  return (
    <div className={classes.fileContainer}>
      <Tooltip content={i18n.getString(file.message)}>
        <div className={classes.filePreviewImageIcon}>
          <img src={EntryCardSVGIcons.PdfSVGIcon} alt="file-icon" />
          <span>
            {uploadTried && hasError ? (
              <ErrorCircle20Regular
                color={tokens.colorPaletteDarkOrangeBackground3}
              />
            ) : null}
            {uploadTried && !hasError ? (
              <CheckmarkCircle20Regular
                color={tokens.colorPaletteGreenBackground3}
              />
            ) : null}
          </span>
        </div>
      </Tooltip>
      <div className={classes.filePreviewContent}>
        <Text block truncate wrap={false}>
          {file.name}
        </Text>
        <ProgressBar value={progress} max={100} />
        <Text>{formatFileSize(file.size, 2, i18n)}</Text>
      </div>
      <Button
        onClick={() => onDeleteFile(file)}
        icon={<Delete16Regular color={tokens.colorPaletteRedBackground3} />}
      />
    </div>
  );
};

FilePreview.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  file: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
    uploaded: PropTypes.bool,
    message: PropTypes.string,
    errorType: PropTypes.string,
  }).isRequired,
  onDeleteFile: PropTypes.func,
  progress: PropTypes.number,
};

FilePreview.defaultProps = {
  progress: 0,
  onDeleteFile: () => {},
};

export const FileUpload = ({
  i18n,
  maxFileSize,
  minFileSize,
  allowedTypes,
  dragOrDropTip,
  disableAddMore,
  dragOrDropLabel,
  onFilesUploaded,
  previewFilesPerRow,
  maxFilesCountAllowed,
  dragOrDropDescription,
}) => {
  const classes = useFileUploadStyles();
  const fileUploadContainerClasses = useDefaultStyles(getFileUploadContainerStyles);
  const fileUploadButtonClasses = useDefaultStyles(getFileUploadButtonStyles);
  const [files, setFiles] = React.useState([]);
  const [uploadError, setUploadError] = React.useState('');

  const legacyI18n = buildLegacyI18n(i18n, [...[ValidationStringMap], ...[LegacyStringMap]]);

  const handleDeleteFile = useCallback(
    (fileToDelete) => {
      setFiles((prevFiles) => {
        const updatedFiles = prevFiles.filter(file => file.id !== fileToDelete.id);
        onFilesUploaded(updatedFiles);
        return updatedFiles;
      });
    },
    [onFilesUploaded]
  );

  const handleFilesUpload = useCallback(
    async (filesToUpload) => {
      try {
        const updatedFiles = await uploadFiles(filesToUpload);
        setFiles((prevFiles) => {
          const updatedFilesArray = [...prevFiles];
          updatedFiles.forEach((updatedFile) => {
            const fileIndex = updatedFilesArray.findIndex(file => file.id === updatedFile.id);
            if (fileIndex > -1) {
              updatedFilesArray[fileIndex] = updatedFile;
            } else {
              updatedFilesArray.push(updatedFile);
            }
          });
          onFilesUploaded(updatedFilesArray);
          return updatedFilesArray;
        });
      } catch (error) {
        setUploadError(error.message || _TL_('Failed to upload files'));
      }
    },
    [onFilesUploaded]
  );

  const onFileRead = useCallback(
    async (fileSources) => {
      try {
        if (!fileSources || !fileSources.length) {
          throw new Error(_TL_('No files selected'));
        }
        const filesToUploadCount = Math.min(
          maxFilesCountAllowed,
          fileSources.length
        );
        const fileSourcesArray = Array.from(fileSources);
        const filesToUpload = fileSourcesArray.slice(0, filesToUploadCount);
        if (
          typeof minFileSize === 'number' ||
          typeof maxFileSize === 'number'
        ) {
          const validationResult = await validateFiles({
            files: filesToUpload,
            maxFileSize,
            minFileSize,
            allowedTypes,
          });
          setFiles(prev => [...prev, ...validationResult.files]);
          handleFilesUpload(validationResult.files);
        }
      } catch (error) {
        setUploadError(error.message);
      }
    },
    [
      setUploadError,
      maxFilesCountAllowed,
      maxFileSize,
      minFileSize,
      allowedTypes,
      handleFilesUpload,
    ]
  );

  const onError = useCallback((error) => {
    setUploadError(error.join(', '));
  }, []);

  return (
    <Stack verticalAlign="start">
      <Stack.Item grow={1} className={fileUploadContainerClasses.container}>
        {files.length ? (
          <div
            className={classes.filesContainer}
            style={{ [filesPerRowInPreviewGrid]: previewFilesPerRow }}
          >
            {files.map((file, index) => (
              <FilePreview
                i18n={i18n}
                file={file}
                progress={60}
                key={file.id || index}
                onDeleteFile={handleDeleteFile}
              />
            ))}
            {maxFilesCountAllowed > 1 && !disableAddMore ? (
              <div className={fileUploadButtonClasses.button}>
                <div className={classes.uploadFilesButton}>
                  <AddSquare24Regular />
                  <Text>{i18n.getString(_TL_('Add more'))}</Text>
                </div>
                <FilePicker
                  useButton
                  enableMultiple
                  hideDescription
                  i18n={legacyI18n}
                  enableDrag={false}
                  showErrors={onError}
                  onFileRead={onFileRead}
                  allowedFileExtensions={allowedTypes}
                />
              </div>
            ) : null}
          </div>
        ) : (
          <FilePicker
            enableDrag
            hideDescription
            i18n={legacyI18n}
            showErrors={onError}
            onFileRead={onFileRead}
            allowedFileExtensions={allowedTypes}
            enableMultiple={maxFilesCountAllowed > 1}
            fileDraggerTip={i18n.getString(dragOrDropTip)}
            fileDraggerLabel={i18n.getString(dragOrDropLabel)}
            fileDraggerDropDescription={i18n.getString(dragOrDropDescription)}
          />
        )}
      </Stack.Item>
      {uploadError ? (
        <Stack.Item className={classes.fileUploadError}>
          <Text variant="error">{i18n.getString(uploadError)}</Text>
        </Stack.Item>
      ) : null}
    </Stack>
  );
};

FileUpload.propTypes = {
  i18n: PropTypes.shape({ getString: func }).isRequired,
  maxFilesCountAllowed: PropTypes.number,
  minFileSize: PropTypes.number,
  maxFileSize: PropTypes.number,
  disableAddMore: PropTypes.bool,
  previewFilesPerRow: PropTypes.number,
  allowedTypes: PropTypes.arrayOf(PropTypes.string),
  dragOrDropLabel: PropTypes.string,
  dragOrDropTip: PropTypes.string,
  dragOrDropDescription: PropTypes.string,
  onFilesUploaded: PropTypes.func.isRequired,
};

FileUpload.defaultProps = {
  maxFilesCountAllowed: 1,
  previewFilesPerRow: 1,
  minFileSize: 0,
  maxFileSize: 10 * 1024 * 1024,
  dragOrDropTip: '',
  allowedTypes: ['*/*'],
  disableAddMore: false,
  dragOrDropDescription: _TL_('Drop your files to upload'),
  dragOrDropLabel: _TL_('Upload or drag and drop your files here.'),
};
